/**
 * The backend's one response envelope (BE `app/core/response.py`):
 *
 *   read (GET):  { statusCode, status: 'Success', data }
 *   write:       { statusCode, status: 'Success', message, data }
 *   error:       { statusCode, status: 'Error', message }
 *
 * `baseQueryWithReauth` hands queries their `data` directly; mutations resolve to the whole
 * write envelope (`ApiMessage<T>`) so callers can show its `message`.
 */
export type ResponseStatus = 'Success' | 'Error';

export interface ApiData<T> {
  statusCode: number;
  status: 'Success';
  data: T;
}

/** What every mutation resolves to: the server's toast text plus its payload. */
export interface ApiMessage<T = null> {
  statusCode: number;
  status: 'Success';
  message: string;
  data: T;
}

export interface ApiError {
  statusCode: number;
  status: 'Error';
  message: string;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** True for any envelope body (success or error). Binary/blob bodies are not envelopes. */
export function isEnvelope(value: unknown): value is ApiData<unknown> | ApiMessage<unknown> | ApiError {
  return (
    isObject(value) &&
    typeof value.statusCode === 'number' &&
    (value.status === 'Success' || value.status === 'Error')
  );
}

export function isApiError(value: unknown): value is ApiError {
  return isEnvelope(value) && value.status === 'Error' && typeof value.message === 'string';
}
